import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useApi } from '../../../../hooks/useApi';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import {
  mapRepricerListProducts, mapRepricerGetMedianYield, mapRepricerSetMedianYield, mapRepricerSaveProduct, mapRepricerDeleteProduct,
  mapRepricerCopyFromMonitoring, mapRepricerListRuns, mapRepricerRunItems,
  mapRepricerLivePrices,
} from '../../../../services/ozonPrices';
import { vitrineGetSettings, vitrineSaveSettings } from '../../../../services/ozonVitrine';
import './MapRepricerPanel.css';
import { fmtDateTime } from '../../../../utils/ruDates';
import { filterBySearch, matchesSearch } from '../../../../utils/search';

function fmtDT(iso) {
  if (!iso) return '—';
  try {
    return fmtDateTime(iso);
  } catch { return iso; }
}

function fmtPrice(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}

// Статус строки: то, что решил цикл репрайсера, а до первой проверки — оценка по текущей витрине.
function rowStatus(row) {
  if (row._draft || row._dirty) return { tone: 'idle', label: 'Не сохранено', hint: 'Сохраните строку, чтобы репрайсер начал работу' };
  if (!row.is_active) return { tone: 'idle', label: 'Выключен', hint: 'Репрайсер по этому товару не работает' };
  const strict = STRICT_MODES.includes(row.mode);
  const bump = strict
    ? (row.target_offset_rub > 0 ? ` (зазор ${row.mode === 'strict_mrc' ? '+' : '−'}${row.target_offset_rub} ₽)` : '')
    : (row.target_bump_pct > 0 ? ` (цель +${row.target_bump_pct}%)` : '');
  // Границы, относительно которых считается «выше/ниже», у каждого режима свои.
  const edgeUp = row.mode === 'strict_mrc' ? 'МРЦ' : 'РРЦ';
  const edgeDown = row.mode === 'strict_rrc' ? 'РРЦ' : 'МРЦ';
  const checked = row.last_checked_at ? `Проверено: ${fmtDT(row.last_checked_at)}` : '';
  const next = row.next_check_at ? `Следующий переобход: ${fmtDT(row.next_check_at)}` : '';
  const hint = [row.last_error, checked, next].filter(Boolean).join('\n');
  switch (row.status) {
    case 'ok': return { tone: 'ok', label: `Успешно${bump}`, hint };
    case 'above': return { tone: 'bad', label: `Цена выше ${edgeUp}${bump}`, hint };
    case 'below': return { tone: 'bad', label: `Цена ниже ${edgeDown}${bump}`, hint };
    case 'waiting': return { tone: 'wait', label: 'Ждём переобход', hint };
    case 'warning': return { tone: 'warn', label: `Предупреждение${bump}`, hint };
    case 'error': return { tone: 'err', label: 'Ошибка', hint };
    default: break;
  }
  // Оценка до первой проверки — по последней известной витрине, по правилам режима.
  const mrc = Number(row.mrc);
  const rrc = Number(row.rrc);
  const v = row.current_vitrine_price;
  const seen = 'По последней витрине. Репрайсер ещё не правил цену.';
  if (v != null && strict) {
    const edge = row.mode === 'strict_mrc' ? mrc : rrc;
    if (!(edge > 0)) return { tone: 'idle', label: 'Нет данных', hint: `Заполните ${edgeDown}` };
    if (Math.round(v) === Math.round(edge)) return { tone: 'ok', label: 'Успешно', hint: `По последней витрине цена равна ${edgeUp}` };
    return { tone: 'bad', label: `Цена ${v > edge ? 'выше' : 'ниже'} ${row.mode === 'strict_mrc' ? 'МРЦ' : 'РРЦ'}`, hint: seen };
  }
  if (v != null && mrc > 0 && rrc > 0) {
    if (v > rrc) return { tone: 'bad', label: 'Цена выше РРЦ', hint: seen };
    if (v < mrc) return { tone: 'bad', label: 'Цена ниже МРЦ', hint: seen };
    return { tone: 'ok', label: 'Успешно', hint: 'По последней витрине цена в пределах МРЦ/РРЦ' };
  }
  return { tone: 'idle', label: 'Нет данных', hint: row.last_error || 'Витрина по товару ещё не проверялась' };
}

function draftProduct(offer_id) {
  return {
    offer_id, mrc: '', rrc: '', mode: 'range', price_basis: 'with_card',
    is_active: true, competitor_skus: [], competitor_price_delta_rubles: '',
    promo_action: 'warn',
    current_price: null, current_vitrine_price: null, current_vitrine_checked_at: null,
    stock_fbo: null, stock_fbs: null,
    _draft: true,
  };
}

const MODE_LABEL = {
  range: 'Диапазон МРЦ–РРЦ',
  strict_mrc: 'Жёсткое МРЦ',
  strict_rrc: 'Жёсткое РРЦ',
  competitors: 'Сравнение с конкурентами',
};
const STRICT_MODES = ['strict_mrc', 'strict_rrc'];
const BASIS_LABEL = { with_card: 'С картой', without_card: 'Без карты' };
// Акция держит витрину своей акционной ценой — правка цены продавца её не поднимает.
const PROMO_ACTION_LABEL = {
  warn: 'Только предупредить',
  raise_price: 'Поднять акционную цену',
  exit: 'Поднять или вывести из акции',
};

// ── Template upload/download (XLSX) ─────────────────────────────────────

const TEMPLATE_HEADERS = [
  'Артикул', 'МРЦ', 'РРЦ',
  'Режим (Диапазон МРЦ–РРЦ / Жёсткое МРЦ / Жёсткое РРЦ / Сравнение с конкурентами)',
  'Цена (С картой / Без карты)',
  'Конкуренты (через запятую)',
  'Разница с конкурентом, ₽ (можно отрицательную)',
  'Активен (Да/Нет)',
  'Акция ниже МРЦ (Только предупредить / Поднять акционную цену / Поднять или вывести из акции)',
];

function parseMode(v) {
  const s = String(v || '').trim().toLowerCase();
  if (s.includes('конкур') || s === 'competitors') return 'competitors';
  if (s.includes('жёсткое ррц') || s.includes('жесткое ррц') || s === 'strict_rrc') return 'strict_rrc';
  if (s.includes('жёсткое мрц') || s.includes('жесткое мрц') || s === 'strict_mrc') return 'strict_mrc';
  return 'range';
}
function parseBasis(v) {
  const s = String(v || '').trim().toLowerCase();
  return s.includes('без') || s === 'without_card' ? 'without_card' : 'with_card';
}
function parsePromoAction(v) {
  const s = String(v || '').trim().toLowerCase();
  if (s.includes('вывести') || s === 'exit') return 'exit';
  if (s.includes('поднять') || s === 'raise_price') return 'raise_price';
  return 'warn';
}
function parseActive(v) {
  if (v === undefined || v === null || v === '') return true;
  const s = String(v).trim().toLowerCase();
  return !(s === 'нет' || s === 'false' || s === '0' || s === 'no');
}

function downloadTemplate(products) {
  const rows = [TEMPLATE_HEADERS];
  const source = products.length ? products : [{
    offer_id: 'Aspen90S-NTR', mrc: 3500, rrc: 3900, mode: 'range',
    price_basis: 'with_card', competitor_skus: [], competitor_price_delta_rubles: '', is_active: true,
    promo_action: 'warn',
  }];
  for (const p of source) {
    rows.push([
      p.offer_id, p.mrc, p.rrc,
      MODE_LABEL[p.mode] || MODE_LABEL.range,
      BASIS_LABEL[p.price_basis] || BASIS_LABEL.with_card,
      (p.competitor_skus || []).join(', '),
      p.competitor_price_delta_rubles ?? '',
      p.is_active ? 'Да' : 'Нет',
      PROMO_ACTION_LABEL[p.promo_action] || PROMO_ACTION_LABEL.warn,
    ]);
  }
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 34 }, { wch: 20 }, { wch: 30 }, { wch: 18 }, { wch: 14 }, { wch: 32 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Соблюдение РРЦ');
  XLSX.writeFile(wb, 'map_repricer_template.xlsx');
}

function parseUploadedRows(sheetRows) {
  const [, ...dataRows] = sheetRows;
  return dataRows
    .filter(r => r && r[0] != null && String(r[0]).trim())
    .map(r => ({
      offer_id: String(r[0]).trim(),
      mrc: r[1] != null ? String(r[1]).trim() : '',
      rrc: r[2] != null ? String(r[2]).trim() : '',
      mode: parseMode(r[3]),
      price_basis: parseBasis(r[4]),
      competitor_skus: String(r[5] || '').split(/[,;\n]+/).map(s => s.trim()).filter(Boolean),
      competitor_price_delta_rubles: r[6] != null && String(r[6]).trim() !== '' ? String(r[6]).trim() : '',
      is_active: parseActive(r[7]),
      promo_action: parsePromoAction(r[8]),
    }));
}

// ── Add product picker ──────────────────────────────────────────────────

function AddProductPicker({ allProducts, existingIds, onAdd }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    let list = allProducts.filter(p => !existingIds.has(p.offer_id));
    if (search.trim()) {
      list = filterBySearch(list, search);
    }
    return list.slice(0, 50);
  }, [allProducts, existingIds, search]);

  if (!open) {
    return (
      <button className="btn btn--ghost btn--sm" onClick={() => setOpen(true)}>
        + Добавить товар
      </button>
    );
  }

  return (
    <div className="mrp-picker">
      <input
        className="mrp-picker__search"
        placeholder="Поиск по артикулу или названию"
        value={search}
        onChange={e => setSearch(e.target.value)}
        autoFocus
      />
      <button className="btn btn--ghost btn--xs" onClick={() => setOpen(false)}>✕</button>
      <div className="mrp-picker__list">
        {filtered.map(p => (
          <div key={p.offer_id} className="mrp-picker__item" onClick={() => { onAdd(p.offer_id); setOpen(false); setSearch(''); }}>
            <span className="mrp-picker__id">{p.offer_id}</span>
            <span className="mrp-picker__name">{p.name || '—'}</span>
          </div>
        ))}
        {filtered.length === 0 && <div className="mrp-picker__empty">Ничего не найдено</div>}
      </div>
    </div>
  );
}

// ── Competitors modal ─────────────────────────────────────────────────────

// SKU конкурента — число из карточки Ozon. Ссылку на товар тоже принимаем: SKU
// стоит в её конце. Артикул продавца или мусор парсер не найдёт, а цель молча
// уехала бы в потолок РРЦ — поэтому такие строки не сохраняем, а называем.
function parseCompetitorTokens(text) {
  const valid = [];
  const invalid = [];
  for (const raw of text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean)) {
    const fromUrl = raw.match(/ozon\.ru\/(?:product|t)\/[^\s?#]*?(\d{6,})\/?(?:[?#]|$)/i);
    const sku = fromUrl ? fromUrl[1] : (/^\d{5,}$/.test(raw) ? raw : null);
    if (!sku) invalid.push(raw);
    else if (!valid.includes(sku)) valid.push(sku);
  }
  return { valid, invalid };
}

function CompetitorsModal({ offerId, skus, onSave, onClose }) {
  const [text, setText] = useState(skus.join('\n'));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const { valid, invalid } = parseCompetitorTokens(text);
    if (invalid.length) {
      setError(`Не похоже на SKU Ozon: ${invalid.join(', ')}. Нужен числовой SKU или ссылка на товар.`);
      return;
    }
    setSaving(true);
    const ok = await onSave(valid);
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <div className="vm-modal-overlay" onClick={onClose}>
      <div className="vm-modal" onClick={e => e.stopPropagation()}>
        <h4 className="vm-modal__title">Конкуренты — {offerId}</h4>
        <div className="vm-modal__body">
          <label className="mrp-comp-editor__label">SKU конкурентов (через запятую или с новой строки)</label>
          <textarea
            className="mrp-comp-editor__area"
            rows={4}
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Например: 1234567890, 9876543210 или ссылка на товар Ozon"
            autoFocus
          />
          {error && <div className="mrp-comp-editor__hint" style={{ color: 'var(--danger, #c0392b)' }}>{error}</div>}
          <div className="mrp-comp-editor__hint">
            Если ни один конкурент не даст цену на момент обхода — цена будет установлена на уровне РРЦ.
          </div>
        </div>
        <div className="vm-modal__actions">
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
          <button className="btn btn--primary btn--sm" onClick={save} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Run history ──────────────────────────────────────────────────────────

const RUN_STATUS_LABEL = { running: 'Выполняется', done: 'Готово', error: 'Ошибка' };
const ITEM_STATUS_LABEL = { ok: 'Изменено', skipped: 'Пропущено', error: 'Ошибка' };

// ── Schedule / auto-run status ──────────────────────────────────────────
//
// The repricer has no check cycle of its own (by design — see
// map_repricer_service.py) — enrolled products ride along in the vitrine
// monitoring batch, and a repricer run fires right after that batch lands.
// So "when does the repricer run" is really "what's the monitoring
// schedule", read here from the same settings the "Мониторинг витрины"
// tab edits, purely for display.

function nextScheduledSlot(scheduleJson) {
  if (!Array.isArray(scheduleJson) || scheduleJson.length !== 168 || !scheduleJson.some(Boolean)) return null;
  const now = new Date();
  for (let offset = 0; offset < 8; offset++) {
    const day = new Date(now);
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() + offset);
    const weekday = (day.getDay() + 6) % 7; // JS Sun=0..Sat=6 → Mon=0..Sun=6 (matches schedule_json layout)
    for (let h = 0; h < 24; h++) {
      if (!scheduleJson[h * 7 + weekday]) continue;
      const slot = new Date(day);
      slot.setHours(h, 0, 0, 0);
      if (slot > now) return slot;
    }
  }
  return null;
}

function ScheduleStatus({ accountId, workspaceId }) {
  const { request, showStatus } = useApi();
  const [settings, setSettings] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disabling, setDisabling] = useState(false);
  const aliveRef = useRef(true);

  const loadStatus = useCallback(() => {
    setLoading(true);
    return Promise.all([
      vitrineGetSettings({ request, accountId, workspaceId }).catch(() => null),
      mapRepricerListRuns({ request, accountId, limit: 1 }).catch(() => []),
    ]).then(([s, runs]) => {
      if (!aliveRef.current) return;
      setSettings(s);
      setLastRun((runs || [])[0] || null);
    }).finally(() => { if (aliveRef.current) setLoading(false); });
  }, [request, accountId, workspaceId]);

  useEffect(() => {
    aliveRef.current = true;
    loadStatus();
    return () => { aliveRef.current = false; };
  }, [loadStatus]);

  const handleDisableAuto = async () => {
    if (!settings) return;
    const ok = window.confirm(
      'Это отключит автоматическую проверку витрины и репрайсера по расписанию для всего кабинета. Продолжить?'
    );
    if (!ok) return;
    setDisabling(true);
    try {
      await vitrineSaveSettings({
        request,
        accountId,
        // Те же поля, что сохраняет «Мониторинг витрины», — иначе у локального
        // агента затирался «свой список» товаров.
        payload: {
          scenario_type: settings.scenario_type,
          offer_ids: settings.offer_ids || [],
          region: settings.region,
          mode: settings.mode,
          schedule_json: settings.schedule_json,
          is_active: false,
          executor: settings.executor,
          custom_source: settings.scenario_type === 'custom' ? settings.custom_source : null,
          custom_value: settings.scenario_type === 'custom' ? settings.custom_value : null,
          workspace_id: workspaceId || null,
        },
      });
      showStatus('success', 'Автозапуск выключен');
      await loadStatus();
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      if (aliveRef.current) setDisabling(false);
    }
  };

  if (loading) {
    return <div className="mrp-status mrp-status--loading">Загрузка статуса автозапуска...</div>;
  }

  const autoOn = !!settings && settings.mode === 'auto' && settings.is_active;
  const activeSlots = (settings?.schedule_json || []).filter(Boolean).length;
  const nextSlot = autoOn ? nextScheduledSlot(settings.schedule_json) : null;

  return (
    <div className="mrp-status">
      <span className={`mrp-status__badge${autoOn ? ' mrp-status__badge--on' : ' mrp-status__badge--off'}`}>
        {autoOn ? '● Автозапуск включён' : '○ Автозапуск выключен'}
      </span>
      <span className="mrp-status__item">
        {autoOn
          ? `по расписанию «Мониторинга витрины» — ${activeSlots} ч/нед`
          : 'включите авто-режим на вкладке «Мониторинг витрины», чтобы репрайсер запускался сам'}
      </span>
      {autoOn && (
        <span className="mrp-status__item">
          Ближайший обход: {nextSlot ? fmtDT(nextSlot.toISOString()) : '—'}
        </span>
      )}
      <span className="mrp-status__item">
        Последний запуск репрайсера: {lastRun ? (
          <>
            {fmtDT(lastRun.started_at)}
            {' · '}
            <span className={`mrp-run__status mrp-run__status--${lastRun.status}`}>
              {RUN_STATUS_LABEL[lastRun.status] || lastRun.status}
            </span>
          </>
        ) : 'ещё не было'}
      </span>
      {autoOn && (
        <button
          type="button"
          className="btn btn--ghost btn--sm mrp-status__disable-btn"
          onClick={handleDisableAuto}
          disabled={disabling}
        >
          {disabling ? 'Отключаем...' : 'Убрать автопроверку'}
        </button>
      )}
    </div>
  );
}

function RunHistory({ accountId }) {
  const { request, showStatus } = useApi();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [items, setItems] = useState({});

  useEffect(() => {
    setLoading(true);
    mapRepricerListRuns({ request, accountId })
      .then(setRuns)
      .catch(() => showStatus('error', 'Ошибка загрузки истории'))
      .finally(() => setLoading(false));
  }, [request, accountId]); // eslint-disable-line

  const toggle = async (runId) => {
    if (expanded === runId) { setExpanded(null); return; }
    setExpanded(runId);
    if (!items[runId]) {
      try {
        const data = await mapRepricerRunItems({ request, accountId, runId });
        setItems(prev => ({ ...prev, [runId]: data }));
      } catch {
        showStatus('error', 'Ошибка загрузки деталей запуска');
      }
    }
  };

  if (loading) return <div className="vm__empty">Загрузка...</div>;
  if (!runs.length) return <div className="vm__empty">Запусков пока не было</div>;

  return (
    <div className="mrp-runs">
      {runs.map(run => (
        <div key={run.id} className="mrp-run">
          <div className="mrp-run__head" onClick={() => toggle(run.id)}>
            <span className="mrp-run__dt">{fmtDT(run.started_at)}</span>
            <span className={`mrp-run__status mrp-run__status--${run.status}`}>
              {RUN_STATUS_LABEL[run.status] || run.status}
            </span>
            <span className="mrp-run__stat">Изменено: {run.total_updated}</span>
            <span className="mrp-run__stat">Пропущено: {run.total_skipped}</span>
            {run.total_errors > 0 && <span className="mrp-run__stat mrp-run__stat--err">Ошибок: {run.total_errors}</span>}
            <span className="mrp-run__chevron">{expanded === run.id ? '▲' : '▼'}</span>
          </div>
          {run.error_msg && <div className="mrp-run__error">{run.error_msg}</div>}
          {expanded === run.id && (
            <div className="mrp-run__items">
              {(items[run.id] || []).map((it, i) => (
                <div key={i} className={`mrp-run__item mrp-run__item--${it.status}`}>
                  <span className="mrp-run__item-id">
                    {it.offer_id}
                    {it.product_name && <span className="mrp-run__item-name">{it.product_name}</span>}
                  </span>
                  {it.attempt > 0 && <span className="mrp-run__item-attempt" title="Проход подряд по этому артикулу">№{it.attempt}</span>}
                  <span className="mrp-run__item-badge">{ITEM_STATUS_LABEL[it.status] || it.status}</span>
                  <span className="mrp-run__item-detail">
                    витрина: {fmtPrice(it.our_vitrine_price)}
                    {it.competitor_min_price != null && <> · мин. конкурент: {fmtPrice(it.competitor_min_price)}</>}
                    {it.target_price != null && <> · цель: {fmtPrice(it.target_price)}</>}
                    {it.promo_action_name && <> · акция: {it.promo_action_name}</>}
                  </span>
                  <span className="mrp-run__item-price">
                    {it.action_taken === 'promo_exit'
                      ? 'вывод из акции'
                      : it.action_taken === 'promo_price'
                        ? <>акционная → {fmtPrice(it.promo_price_after)}</>
                        : it.seller_price_before != null
                          ? (it.seller_price_after != null
                            ? <>{fmtPrice(it.seller_price_before)} → {fmtPrice(it.seller_price_after)}</>
                            : fmtPrice(it.seller_price_before))
                          : '—'}
                  </span>
                  {it.note && <span className="mrp-run__item-note">{it.note}</span>}
                </div>
              ))}
              {!(items[run.id] || []).length && <div className="mrp-run__items-empty">Загрузка...</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────

export default function MapRepricerPanel({ accountId, workspaceId, allProducts }) {
  const { request, showStatus } = useApi();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [copying, setCopying] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [stockFilter, setStockFilter] = useState('all'); // 'all' | 'fbs' | 'fbo' | 'none'
  const [competitorsFor, setCompetitorsFor] = useState(null); // offer_id or null
  const fileInputRef = useRef(null);

  const nameFor = useMemo(() => {
    const m = {};
    for (const p of allProducts || []) m[p.offer_id] = p.name;
    return m;
  }, [allProducts]);

  const skuFor = useMemo(() => {
    const m = {};
    for (const p of allProducts || []) m[p.offer_id] = p.sku;
    return m;
  }, [allProducts]);

  const load = useCallback(() => {
    setLoading(true);
    mapRepricerListProducts({ request, accountId })
      .then(setProducts)
      .catch(() => showStatus('error', 'Ошибка загрузки настроек репрайсера'))
      .finally(() => setLoading(false));
  }, [request, accountId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const [medianYield, setMedianYield] = useState(false);
  const [medianYieldSaving, setMedianYieldSaving] = useState(false);

  useEffect(() => {
    mapRepricerGetMedianYield({ request, accountId }).then(setMedianYield).catch(() => {});
  }, [request, accountId]);

  const toggleMedianYield = useCallback(async (enabled) => {
    setMedianYieldSaving(true);
    try {
      setMedianYield(await mapRepricerSetMedianYield({ request, accountId, enabled }));
    } catch {
      showStatus('error', 'Не удалось сохранить настройку');
    } finally {
      setMedianYieldSaving(false);
    }
  }, [request, accountId]); // eslint-disable-line

  const existingIds = useMemo(() => new Set(products.map(p => p.offer_id)), [products]);
  const dirtyCount = useMemo(() => products.filter(p => p._draft || p._dirty).length, [products]);

  // Draft rows (just added / copied / uploaded) have no price data yet —
  // they're not saved as MapRepricerProduct rows, so list_products_with_live_data
  // never sees them. Fetch it separately for exactly these offer_ids and merge in.
  const fetchLiveDataFor = async (offerIds) => {
    if (!offerIds.length) return;
    try {
      const items = await mapRepricerLivePrices({ request, accountId, offerIds });
      setProducts(prev => prev.map(p => (
        items[p.offer_id] ? { ...p, ...items[p.offer_id] } : p
      )));
    } catch {
      // Non-critical — the table still works without live data, just emptier.
    }
  };

  const addProduct = (offerId) => {
    setProducts(prev => [...prev, draftProduct(offerId)]);
    fetchLiveDataFor([offerId]);
  };

  const addAllProducts = () => {
    const toAdd = (allProducts || []).map(p => p.offer_id).filter(id => !existingIds.has(id));
    if (!toAdd.length) {
      showStatus('info', 'Все товары уже добавлены в репрайсер');
      return;
    }
    setProducts(prev => [...prev, ...toAdd.map(draftProduct)]);
    showStatus('success', `Добавлено ${toAdd.length} товаров — заполните МРЦ/РРЦ и сохраните`);
    fetchLiveDataFor(toAdd);
  };

  const copyFromMonitoring = async () => {
    setCopying(true);
    try {
      const data = await mapRepricerCopyFromMonitoring({ request, accountId });
      const toAdd = (data.offer_ids || []).filter(id => !existingIds.has(id));
      if (!toAdd.length) {
        showStatus('info', 'Все товары мониторинга уже добавлены в репрайсер');
      } else {
        setProducts(prev => [...prev, ...toAdd.map(draftProduct)]);
        showStatus('success', `Добавлено ${toAdd.length} товаров из мониторинга — заполните МРЦ/РРЦ и сохраните`);
        fetchLiveDataFor(toAdd);
      }
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setCopying(false);
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const sheetRows = XLSX.utils.sheet_to_json(ws, { header: 1 });
      const parsed = parseUploadedRows(sheetRows);
      if (!parsed.length) { showStatus('error', 'Файл пустой или не распознан'); return; }
      setProducts(prev => {
        const byId = new Map(prev.map(p => [p.offer_id, p]));
        for (const row of parsed) {
          const existing = byId.get(row.offer_id);
          byId.set(row.offer_id, {
            ...draftProduct(row.offer_id),
            ...existing,
            ...row,
            _draft: !existing,
            _dirty: true,
          });
        }
        return Array.from(byId.values());
      });
      showStatus('success', `Загружено ${parsed.length} строк из файла — проверьте и сохраните`);
      fetchLiveDataFor(parsed.map(row => row.offer_id));
    } catch (err) {
      showStatus('error', 'Не удалось прочитать файл: ' + err.message);
    }
  };

  const updateProduct = (offerId, patch) => {
    setProducts(prev => prev.map(p => (p.offer_id === offerId ? { ...p, ...patch } : p)));
  };

  const saveProduct = async (p) => {
    const mrcNum = Number(p.mrc);
    const rrcNum = Number(p.rrc);
    const hasMrc = p.mrc !== '' && p.mrc != null && Number.isFinite(mrcNum) && mrcNum > 0;
    const hasRrc = p.rrc !== '' && p.rrc != null && Number.isFinite(rrcNum) && rrcNum > 0;
    if (hasMrc && hasRrc && mrcNum > rrcNum) {
      showStatus('error', `${p.offer_id}: МРЦ не может быть больше РРЦ`);
      return false;
    }
    // Товар без заполненных границ сохраняется, но не может быть активным —
    // репрайсеру не с чем сравнивать цену. Жёсткому режиму нужна только своя граница.
    const complete = p.mode === 'strict_mrc' ? hasMrc : p.mode === 'strict_rrc' ? hasRrc : hasMrc && hasRrc;
    const effectiveActive = p.is_active && complete;
    const deltaNum = Number(p.competitor_price_delta_rubles);
    const hasDelta = p.competitor_price_delta_rubles !== '' && p.competitor_price_delta_rubles != null && Number.isFinite(deltaNum);
    setSavingId(p.offer_id);
    try {
      const saved = await mapRepricerSaveProduct({
        request, accountId, offerId: p.offer_id,
        payload: {
          offer_id: p.offer_id,
          mrc: hasMrc ? mrcNum : 0,
          rrc: hasRrc ? rrcNum : 0,
          mode: p.mode, price_basis: p.price_basis,
          is_active: effectiveActive, competitor_skus: p.competitor_skus || [],
          competitor_price_delta_rubles: hasDelta ? deltaNum : null,
          promo_action: p.promo_action || 'warn',
        },
      });
      // The save endpoint doesn't recompute live OZON price / vitrine snapshot data —
      // it echoes those fields back as null. Keep whatever was already loaded for
      // this row instead of letting the save response blank it out.
      setProducts(prev => prev.map(x => (x.offer_id === p.offer_id ? {
        ...x, ...saved,
        current_price: saved.current_price ?? x.current_price,
        current_vitrine_price: saved.current_vitrine_price ?? x.current_vitrine_price,
        current_vitrine_checked_at: saved.current_vitrine_checked_at ?? x.current_vitrine_checked_at,
        stock_fbo: saved.stock_fbo ?? x.stock_fbo,
        stock_fbs: saved.stock_fbs ?? x.stock_fbs,
        is_active: effectiveActive,
        _draft: false, _dirty: false,
      } : x)));
      return { ok: true, forcedInactive: p.is_active && !complete };
    } catch (e) {
      showStatus('error', `${p.offer_id}: ${e.message}`);
      return { ok: false };
    } finally {
      setSavingId(null);
    }
  };

  const saveOne = async (p) => {
    const { ok, forcedInactive } = await saveProduct(p);
    if (ok) {
      showStatus('success', forcedInactive
        ? 'Сохранено — без МРЦ/РРЦ товар оставлен неактивным'
        : 'Сохранено');
    }
  };

  const saveAllDirty = async () => {
    const dirty = products.filter(p => p._draft || p._dirty);
    if (!dirty.length) return;
    setSavingAll(true);
    let okCount = 0;
    let forcedCount = 0;
    for (const p of dirty) {
      const { ok, forcedInactive } = await saveProduct(p);
      if (ok) {
        okCount += 1;
        if (forcedInactive) forcedCount += 1;
      }
    }
    setSavingAll(false);
    const suffix = forcedCount > 0 ? ` (${forcedCount} без МРЦ/РРЦ оставлены неактивными)` : '';
    showStatus(okCount === dirty.length ? 'success' : 'error', `Сохранено ${okCount} из ${dirty.length}${suffix}`);
  };

  const removeProduct = async (p) => {
    if (p._draft) {
      setProducts(prev => prev.filter(x => x.offer_id !== p.offer_id));
      return;
    }
    try {
      await mapRepricerDeleteProduct({ request, accountId, offerId: p.offer_id });
      setProducts(prev => prev.filter(x => x.offer_id !== p.offer_id));
      showStatus('success', 'Удалено');
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    }
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    if (activeFilter === 'active') list = list.filter(p => p.is_active);
    else if (activeFilter === 'inactive') list = list.filter(p => !p.is_active);
    if (stockFilter === 'fbs') list = list.filter(p => !!p.stock_fbs);
    else if (stockFilter === 'fbo') list = list.filter(p => !!p.stock_fbo);
    else if (stockFilter === 'none') list = list.filter(p => !p.stock_fbo && !p.stock_fbs);
    if (search.trim()) {
      list = list.filter(p => matchesSearch([p, nameFor[p.offer_id], skuFor[p.offer_id]], search));
    }
    return list;
  }, [products, search, nameFor, skuFor, activeFilter, stockFilter]);

  const columns = useMemo(() => [
    { key: 'offer_id', header: 'Артикул', width: 150, type: 'text', sticky: true },
    {
      key: '_name', header: 'Название', width: 240, sortable: false,
      render: (_v, row) => nameFor[row.offer_id] || '—',
    },
    {
      key: '_sku', header: 'SKU', width: 110, sortable: false,
      render: (_v, row) => {
        const sku = skuFor[row.offer_id];
        return sku
          ? (
            <a
              href={`https://www.ozon.ru/product/${sku}`}
              target="_blank" rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {sku}
            </a>
          )
          : '—';
      },
    },
    {
      key: 'current_price', header: 'Тек. цена', width: 100, type: 'number',
      render: v => fmtPrice(v),
    },
    {
      key: 'current_vitrine_price', header: 'Тек. витрина', width: 110, type: 'number',
      render: (v, row) => v != null
        ? <span title={row.current_vitrine_checked_at ? fmtDT(row.current_vitrine_checked_at) : ''}>{fmtPrice(v)}</span>
        : <span className="vm-pivot__na">нет данных</span>,
    },
    {
      key: '_stock', header: 'Остатки', width: 130, sortable: false,
      render: (_v, row) => {
        const fbo = row.stock_fbo;
        const fbs = row.stock_fbs;
        if (fbo == null && fbs == null) return <span className="vm-pivot__na">нет данных</span>;
        if (!fbo && !fbs) return <span className="mrp-stock__zero">нет остатков</span>;
        return (
          <span className="mrp-stock">
            {!!fbo && <span className="mrp-stock__badge mrp-stock__badge--fbo">FBO {fbo}</span>}
            {!!fbs && <span className="mrp-stock__badge mrp-stock__badge--fbs">FBS {fbs}</span>}
          </span>
        );
      },
    },
    {
      key: '_status', header: 'Статус', width: 170, sortable: false,
      render: (_v, row) => {
        const st = rowStatus(row);
        return (
          <span className={`mrp-state mrp-state--${st.tone}`} title={st.hint}>
            {st.label}
          </span>
        );
      },
    },
    {
      key: 'mrc', header: 'МРЦ', width: 120, type: 'number',
      render: (v, row) => (
        <input
          type="number" min="0" step="1" className="mrp-table__input"
          value={v}
          onClick={e => e.stopPropagation()}
          onChange={e => updateProduct(row.offer_id, { mrc: e.target.value, _dirty: true })}
        />
      ),
    },
    {
      key: 'rrc', header: 'РРЦ', width: 120, type: 'number',
      render: (v, row) => (
        <input
          type="number" min="0" step="1" className="mrp-table__input"
          value={v}
          onClick={e => e.stopPropagation()}
          onChange={e => updateProduct(row.offer_id, { rrc: e.target.value, _dirty: true })}
        />
      ),
    },
    {
      key: 'price_basis', header: 'Цена', width: 110, sortable: false,
      render: (v, row) => (
        <select
          className="mrp-table__select"
          value={v}
          onClick={e => e.stopPropagation()}
          onChange={e => updateProduct(row.offer_id, { price_basis: e.target.value, _dirty: true })}
        >
          <option value="with_card">С картой</option>
          <option value="without_card">Без карты</option>
        </select>
      ),
    },
    {
      key: 'mode', header: 'Режим', width: 220, sortable: false,
      render: (v, row) => (
        <div onClick={e => e.stopPropagation()}>
          <select
            className="mrp-table__select"
            title={'Диапазон МРЦ–РРЦ — держать витрину внутри коридора, внутри не трогать.\n'
              + 'Жёсткое МРЦ — держать витрину ровно на МРЦ рубль в рубль, с любой стороны (выше МРЦ цена снижается).\n'
              + 'Жёсткое РРЦ — то же про РРЦ.\n'
              + 'Сравнение с конкурентами — цель от минимального конкурента, в границах МРЦ/РРЦ.'}
            value={v}
            onChange={e => updateProduct(row.offer_id, { mode: e.target.value, _dirty: true })}
          >
            <option value="range">Диапазон МРЦ–РРЦ</option>
            <option value="strict_mrc">Жёсткое МРЦ</option>
            <option value="strict_rrc">Жёсткое РРЦ</option>
            <option value="competitors">Сравнение с конкурентами</option>
          </select>
          {STRICT_MODES.includes(v) && row.target_offset_rub > 0 && (
            <div className="mrp-table__delta" title="Около круглых порогов соинвест Ozon перескакивает процентом и ровно на границу витрина не встаёт. Зазор уводит цель в безопасную сторону на рубль за проход.">
              <span className="mrp-table__delta-label">
                зазор {v === 'strict_mrc' ? '+' : '−'}{row.target_offset_rub} ₽
              </span>
            </div>
          )}
          {v === 'competitors' && (
            <>
              <button className="mrp-table__comp-btn" onClick={() => setCompetitorsFor(row.offer_id)}>
                {(row.competitor_skus || []).length} конкур. ✎
              </button>
              <div className="mrp-table__delta" title="На сколько рублей наша цена будет отличаться от цены конкурента (можно указать отрицательное значение, чтобы быть ниже)">
                <input
                  type="number" step="1" className="mrp-table__input mrp-table__input--delta"
                  placeholder="±₽"
                  value={row.competitor_price_delta_rubles ?? ''}
                  onClick={e => e.stopPropagation()}
                  onChange={e => updateProduct(row.offer_id, { competitor_price_delta_rubles: e.target.value, _dirty: true })}
                />
                <span className="mrp-table__delta-label">₽ к конкуренту</span>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'promo_action', header: 'Если в акции', width: 190, sortable: false,
      render: (v, row) => (
        <select
          className="mrp-table__select"
          title={'В акции витрину задаёт акционная цена, а не цена продавца — поднять цену продавца '
            + 'бесполезно. Что делать, если акционная цена ниже МРЦ:\n'
            + '· Только предупредить — цена не меняется, товар помечается предупреждением;\n'
            + '· Поднять акционную цену — до МРЦ, если акция это допускает;\n'
            + '· Поднять или вывести из акции — если акция цену на уровне МРЦ не допускает, товар выводится из акции.'}
          value={v || 'warn'}
          onClick={e => e.stopPropagation()}
          onChange={e => updateProduct(row.offer_id, { promo_action: e.target.value, _dirty: true })}
        >
          <option value="warn">Только предупредить</option>
          <option value="raise_price">Поднять акционную цену</option>
          <option value="exit">Поднять или вывести из акции</option>
        </select>
      ),
    },
    {
      key: 'is_active', header: 'Активен', width: 80, align: 'center', sortable: false,
      render: (v, row) => (
        <input
          type="checkbox"
          checked={v}
          onClick={e => e.stopPropagation()}
          onChange={e => updateProduct(row.offer_id, { is_active: e.target.checked, _dirty: true })}
        />
      ),
    },
    {
      key: '_actions', header: 'Действия', width: 150, sortable: false, exportable: false,
      render: (_v, row) => (
        <div className="mrp-table__actions" onClick={e => e.stopPropagation()}>
          <button className="btn btn--primary btn--xs" onClick={() => saveOne(row)} disabled={savingId === row.offer_id}>
            {savingId === row.offer_id ? '...' : 'Сохранить'}
          </button>
          <button className="btn btn--ghost btn--xs mrp-table__del" onClick={() => removeProduct(row)}>✕</button>
        </div>
      ),
    },
  ], [nameFor, skuFor, savingId]); // eslint-disable-line

  return (
    <div className="mrp">
      <div className="mrp__header">
        <p className="mrp__desc">
          МРЦ — цена не может быть ниже. РРЦ — цена не может быть выше. «Диапазон МРЦ–РРЦ» держит
          витрину внутри коридора, «Жёсткое МРЦ» и «Жёсткое РРЦ» — ровно на своей границе, рубль
          в рубль, с любой стороны. Товары под контролем автоматически едут в общий обход
          «Мониторинга цены» (без фильтра по остаткам), и расчёт запускается сразу после его завершения.
        </p>
        <ScheduleStatus accountId={accountId} workspaceId={workspaceId} />
        <label className="mrp__desc" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={medianYield} disabled={medianYieldSaving}
            onChange={e => toggleMedianYield(e.target.checked)} />
          <span>
            Не конфликтовать с корректировкой медианы. РРЦ не работает между ночным и дневным запуском
            медианы (время из её настроек, МСК), а медиана ведёт и эти товары. После дневного запуска
            РРЦ снова возвращает витрину в границы.
          </span>
        </label>
        <div className="mrp__tabs">
          <button className={`vm__tab${tab === 'products' ? ' vm__tab--active' : ''}`} onClick={() => setTab('products')}>
            Товары
          </button>
          <button className={`vm__tab${tab === 'history' ? ' vm__tab--active' : ''}`} onClick={() => setTab('history')}>
            История запусков
          </button>
        </div>
      </div>

      {tab === 'products' && (
        <>
          <div className="mrp__toolbar">
            <AddProductPicker allProducts={allProducts || []} existingIds={existingIds} onAdd={addProduct} />
            <button className="btn btn--ghost btn--sm" onClick={addAllProducts}>
              + Добавить все товары
            </button>
            <button className="btn btn--ghost btn--sm" onClick={copyFromMonitoring} disabled={copying}>
              {copying ? '...' : 'Скопировать из мониторинга витрины'}
            </button>
            <button className="btn btn--ghost btn--sm" onClick={() => downloadTemplate(products)}>
              ↓ Скачать шаблон
            </button>
            <button className="btn btn--ghost btn--sm" onClick={handleUploadClick}>
              ↑ Загрузить из файла
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {dirtyCount > 0 && (
              <button className="btn btn--primary btn--sm" onClick={saveAllDirty} disabled={savingAll}>
                {savingAll ? 'Сохранение...' : `Сохранить всё (${dirtyCount})`}
              </button>
            )}
            <button className="btn btn--ghost btn--sm" onClick={load} disabled={loading} style={{ marginLeft: 'auto' }}>
              ↺ Обновить
            </button>
          </div>

          <div className="vm-cfg__toggle mrp__filter">
            <button
              className={`vm-cfg__toggle-btn${activeFilter === 'all' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              Все ({products.length})
            </button>
            <button
              className={`vm-cfg__toggle-btn${activeFilter === 'active' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setActiveFilter('active')}
            >
              Активные ({products.filter(p => p.is_active).length})
            </button>
            <button
              className={`vm-cfg__toggle-btn${activeFilter === 'inactive' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setActiveFilter('inactive')}
            >
              Неактивные ({products.filter(p => !p.is_active).length})
            </button>
          </div>

          <div className="vm-cfg__toggle mrp__filter">
            <button
              className={`vm-cfg__toggle-btn${stockFilter === 'all' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setStockFilter('all')}
            >
              Все остатки
            </button>
            <button
              className={`vm-cfg__toggle-btn${stockFilter === 'fbo' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setStockFilter('fbo')}
            >
              FBO ({products.filter(p => !!p.stock_fbo).length})
            </button>
            <button
              className={`vm-cfg__toggle-btn${stockFilter === 'fbs' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setStockFilter('fbs')}
            >
              FBS ({products.filter(p => !!p.stock_fbs).length})
            </button>
            <button
              className={`vm-cfg__toggle-btn${stockFilter === 'none' ? ' vm-cfg__toggle-btn--on' : ''}`}
              onClick={() => setStockFilter('none')}
            >
              Нет остатков ({products.filter(p => !p.stock_fbo && !p.stock_fbs).length})
            </button>
          </div>

          {!loading && products.length === 0 ? (
            <div className="vm__empty">
              Нет товаров под контролем МРЦ/РРЦ — добавьте вручную, скопируйте из мониторинга или загрузите шаблон
            </div>
          ) : (
            <UnifiedTable
              tableId="map-repricer-products"
              columns={columns}
              data={filteredProducts}
              getRowKey={row => row.offer_id}
              search={search}
              onSearchChange={setSearch}
              exportFilename="map-repricer"
              loading={loading}
            />
          )}

          {competitorsFor && (
            <CompetitorsModal
              offerId={competitorsFor}
              skus={(products.find(p => p.offer_id === competitorsFor)?.competitor_skus) || []}
              onSave={async (skus) => {
                // Список конкурентов сохраняется сразу — раньше он жил только в строке
                // и терялся, если не нажать «Сохранить» в таблице.
                const row = products.find(p => p.offer_id === competitorsFor);
                if (!row) return false;
                updateProduct(competitorsFor, { competitor_skus: skus, _dirty: true });
                const { ok } = await saveProduct({ ...row, competitor_skus: skus });
                if (ok) showStatus('success', 'Конкуренты сохранены');
                return ok;
              }}
              onClose={() => setCompetitorsFor(null)}
            />
          )}
        </>
      )}

      {tab === 'history' && <RunHistory accountId={accountId} />}
    </div>
  );
}
